import { Palette, Type, Maximize2, Square, Box, Layout } from 'lucide-react';

interface Theme {
  id?: string;
  label: string;
  description: string;
  preview: {
    primary: string;
    secondary: string;
  };
  tokens: {
    colors: Record<string, string>;
    typography: Record<string, string>;
    spacing: Record<string, string>;
    borders: Record<string, string>;
    shadows: Record<string, string>;
    layout: Record<string, string>;
  };
}

interface ThemePreviewProps {
  theme: Theme;
}

export function ThemePreview({ theme }: ThemePreviewProps) {
  // Calculate contrast color (black or white) for text on colored backgrounds
  const getContrastColor = (hexColor: string): string => {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  return (
    <div className="theme-preview">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
          <Palette className="w-5 h-5 text-primary-600" />
          <span>Anteprima Tema</span>
        </h3>
        <p className="text-sm text-gray-600 mt-1">{theme.label}</p>
        <p className="text-xs text-gray-500 mt-0.5">{theme.description}</p>
      </div>

      {/* Preview Swatches (Large) */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 uppercase mb-3">Preview Colors</h4>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div
              className="h-20 rounded-lg border border-gray-300 flex items-center justify-center"
              style={{
                backgroundColor: theme.preview.primary,
                color: getContrastColor(theme.preview.primary),
              }}
            >
              <span className="text-sm font-medium">Primary</span>
            </div>
            <p className="text-xs text-gray-600 mt-1 font-mono text-center">
              {theme.preview.primary}
            </p>
          </div>
          <div>
            <div
              className="h-20 rounded-lg border border-gray-300 flex items-center justify-center"
              style={{
                backgroundColor: theme.preview.secondary,
                color: getContrastColor(theme.preview.secondary),
              }}
            >
              <span className="text-sm font-medium">Secondary</span>
            </div>
            <p className="text-xs text-gray-600 mt-1 font-mono text-center">
              {theme.preview.secondary}
            </p>
          </div>
        </div>
      </div>

      {/* Colors */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-3">
          <Palette className="w-4 h-4 text-primary-600" />
          <h4 className="text-sm font-medium text-gray-700 uppercase">Colors</h4>
          <span className="text-xs text-gray-500">({Object.keys(theme.tokens.colors).length})</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(theme.tokens.colors).map(([key, value]) => (
            <div key={key} className="flex items-center space-x-2">
              <div
                className="w-8 h-8 rounded border border-gray-300 flex-shrink-0"
                style={{ backgroundColor: value }}
                title={value}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 truncate">{key}</p>
                <p className="text-xs text-gray-500 font-mono truncate">{value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Typography */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-3">
          <Type className="w-4 h-4 text-primary-600" />
          <h4 className="text-sm font-medium text-gray-700 uppercase">Typography</h4>
          <span className="text-xs text-gray-500">({Object.keys(theme.tokens.typography).length})</span>
        </div>
        <div className="space-y-3">
          {Object.entries(theme.tokens.typography).map(([key, value]) => (
            <div key={key} className="border border-gray-200 rounded p-3 bg-white">
              <p className="text-xs text-gray-500 mb-1">{key}</p>
              <p
                className="text-sm text-gray-900 truncate"
                style={
                  key.includes('font-')
                    ? { fontFamily: value }
                    : key.includes('size-')
                    ? { fontSize: value }
                    : {}
                }
              >
                {key.includes('font-') ? 'The quick brown fox...' : value}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Spacing */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-3">
          <Maximize2 className="w-4 h-4 text-primary-600" />
          <h4 className="text-sm font-medium text-gray-700 uppercase">Spacing</h4>
          <span className="text-xs text-gray-500">({Object.keys(theme.tokens.spacing).length})</span>
        </div>
        <div className="space-y-2">
          {Object.entries(theme.tokens.spacing).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{key}</span>
              <span className="text-gray-900 font-mono">{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Borders */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-3">
          <Square className="w-4 h-4 text-primary-600" />
          <h4 className="text-sm font-medium text-gray-700 uppercase">Borders</h4>
          <span className="text-xs text-gray-500">({Object.keys(theme.tokens.borders).length})</span>
        </div>
        <div className="space-y-2">
          {Object.entries(theme.tokens.borders).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{key}</span>
              <div className="flex items-center space-x-2">
                <div
                  className="w-8 h-8 border-2 border-gray-400 bg-gray-100"
                  style={{ borderRadius: value }}
                />
                <span className="text-gray-900 font-mono">{value}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Shadows */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-3">
          <Box className="w-4 h-4 text-primary-600" />
          <h4 className="text-sm font-medium text-gray-700 uppercase">Shadows</h4>
          <span className="text-xs text-gray-500">({Object.keys(theme.tokens.shadows).length})</span>
        </div>
        <div className="space-y-3">
          {Object.entries(theme.tokens.shadows).map(([key, value]) => (
            <div key={key}>
              <p className="text-xs text-gray-500 mb-2">{key}</p>
              <div
                className="w-full h-12 bg-white rounded"
                style={{ boxShadow: value }}
              />
              <p className="text-xs text-gray-600 font-mono mt-1 truncate">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Layout */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-3">
          <Layout className="w-4 h-4 text-primary-600" />
          <h4 className="text-sm font-medium text-gray-700 uppercase">Layout</h4>
          <span className="text-xs text-gray-500">({Object.keys(theme.tokens.layout).length})</span>
        </div>
        <div className="space-y-2">
          {Object.entries(theme.tokens.layout).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{key}</span>
              <span className="text-gray-900 font-mono">{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 uppercase mb-3">Riepilogo</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">Colori</span>
            <p className="font-medium text-gray-900">{Object.keys(theme.tokens.colors).length}</p>
          </div>
          <div>
            <span className="text-gray-500">Tipografia</span>
            <p className="font-medium text-gray-900">{Object.keys(theme.tokens.typography).length}</p>
          </div>
          <div>
            <span className="text-gray-500">Spaziatura</span>
            <p className="font-medium text-gray-900">{Object.keys(theme.tokens.spacing).length}</p>
          </div>
          <div>
            <span className="text-gray-500">Bordi</span>
            <p className="font-medium text-gray-900">{Object.keys(theme.tokens.borders).length}</p>
          </div>
          <div>
            <span className="text-gray-500">Ombre</span>
            <p className="font-medium text-gray-900">{Object.keys(theme.tokens.shadows).length}</p>
          </div>
          <div>
            <span className="text-gray-500">Layout</span>
            <p className="font-medium text-gray-900">{Object.keys(theme.tokens.layout).length}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
